import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useStore } from "@/store/StoreContext";

/**
 * Infinite auto-scrolling track.
 * - speed is multiplied by the admin-controlled scroll speed (1x–5x)
 * - pausable=true: pause on hover / touch, swipeable, arrows
 * - pausable=false: NEVER stops — pure continuous marquee
 */
export default function AutoScroll({ children, speed = 0.5, arrows = true, pausable = true, gapClass = "gap-6", testid = "autoscroll", itemWidth = 320 }) {
  const { content } = useStore();
  const mult = Math.min(Math.max(content?.scroll_speed || 1, 0.5), 5);
  const effSpeed = speed * mult;
  const ref = useRef(null);
  const paused = useRef(false);
  const resumeTimer = useRef(null);
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  const items = React.Children.toArray(children);
  const doubled = [...items, ...items];

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    let raf;
    const step = () => {
      if (!paused.current) {
        el.scrollLeft += effSpeed;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [reduced, effSpeed, items.length]);

  const pause = useCallback(() => { paused.current = true; }, []);
  const resume = useCallback(() => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { paused.current = false; }, 1600);
  }, []);

  const nudge = (dir) => {
    const el = ref.current;
    if (!el) return;
    pause();
    let target = el.scrollLeft + dir * itemWidth;
    const half = el.scrollWidth / 2;
    if (target >= half) target -= half;
    if (target < 0) target += half;
    el.scrollTo({ left: target, behavior: "smooth" });
    resume();
  };

  const interactiveProps = pausable
    ? { onMouseEnter: pause, onMouseLeave: resume, onTouchStart: pause, onTouchEnd: resume }
    : {};

  return (
    <div className="relative group/track" data-testid={testid}>
      <div
        ref={ref}
        {...interactiveProps}
        className={`autoscroll-track flex ${gapClass} overflow-x-auto px-1 py-4`}
        style={{ scrollSnapType: reduced ? "x mandatory" : "none", "--card-w": `${itemWidth}px` }}
      >
        {doubled.map((child, i) => (
          <div key={i} className="shrink-0" style={{ width: "min(var(--card-w), 78vw)" }}>
            {child}
          </div>
        ))}
      </div>
      {arrows && pausable && (
        <>
          <button
            data-testid={`${testid}-prev`}
            onClick={() => nudge(-1)}
            aria-label="Previous"
            className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#B08D2B]/40 bg-white/95 text-[#8F6E1D] opacity-0 shadow-md backdrop-blur transition-opacity duration-300 hover:bg-[#D4AF37] hover:text-[#1C130B] group-hover/track:opacity-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid={`${testid}-next`}
            onClick={() => nudge(1)}
            aria-label="Next"
            className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 hidden md:flex h-11 w-11 items-center justify-center rounded-full border border-[#B08D2B]/40 bg-white/95 text-[#8F6E1D] opacity-0 shadow-md backdrop-blur transition-opacity duration-300 hover:bg-[#D4AF37] hover:text-[#1C130B] group-hover/track:opacity-100"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}
